import { Link } from 'react-router-dom';
import { Icon } from '@/components/brand/Icon';
import { Logo } from '@/components/brand/Logo';
import { useAcademy } from '@/lib/academy';
import { subjectText } from '@/lib/catalogue';
import { useI18n } from '@/lib/i18n';

const LEARN = [
  { slug: 'piano', label: 'Piano' },
  { slug: 'vocal', label: 'Vocal' },
  { slug: 'guitar', label: 'Guitar' },
  { slug: 'electric-guitar', label: 'Electric Guitar' },
  { slug: 'fingerstyle', label: 'Fingerstyle' },
  { slug: 'drums', label: 'Drums' },
  { slug: 'handpan', label: 'Handpan' },
  { slug: 'violin', label: 'Violin' },
];

export function Footer(): React.JSX.Element {
  const { t } = useI18n();
  const academy = useAcademy();

  return (
    <footer className="bg-midnight pb-10 pt-20 text-on-midnight">
      <div className="mv-container flex flex-col gap-14">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 md:gap-10 xl:grid-cols-[300px_repeat(4,1fr)] xl:gap-20">
          <div className="col-span-2 flex flex-col gap-5 md:col-span-3 xl:col-span-1">
            <Logo lockup="stacked" className="h-40 self-start text-on-midnight" />
          </div>

          <FooterColumn title={t('footer.learn')}>
            {LEARN.map((item) => (
              <FooterLink key={item.slug} to={`/lessons/${item.slug}`}>
                {subjectText(t, item.slug, 'name', item.label)}
              </FooterLink>
            ))}
          </FooterColumn>

          <FooterColumn title={t('footer.academy')}>
            <FooterLink to="/teachers">{t('nav.teachers')}</FooterLink>
            <FooterLink to="/practice-rooms">{t('nav.rooms')}</FooterLink>
            <FooterLink to="/community">{t('nav.community')}</FooterLink>
            <FooterLink to="/about">{t('footer.aboutUs')}</FooterLink>
          </FooterColumn>

          <FooterColumn title={t('footer.support')}>
            <FooterLink to="/contact">{t('nav.contact')}</FooterLink>
            <FooterLink to="/faq">{t('footer.faq')}</FooterLink>
            <FooterLink to="/booking-policy">{t('footer.bookingPolicy')}</FooterLink>
            <FooterLink to="/privacy">{t('footer.privacy')}</FooterLink>
          </FooterColumn>

          <FooterColumn title={t('footer.getInTouch')}>
            <a
              href={academy.whatsappUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-2 text-body-sm hover:opacity-70"
            >
              <Icon name="whatsapp" size={18} />
              <span className="whitespace-nowrap">{academy.phoneDisplay}</span>
            </a>
            <a
              href={`mailto:${academy.email}`}
              className="flex items-center gap-2 text-body-sm hover:opacity-70"
            >
              <Icon name="mail" size={18} />
              <span className="min-w-0 break-all">{academy.email}</span>
            </a>
            <a
              href={academy.mapUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-start gap-2 text-body-sm hover:opacity-70"
            >
              <Icon name="map-pin" size={18} className="mt-0.5" />
              {academy.address}
            </a>
          </FooterColumn>
        </div>

        <div className="h-px bg-border-on-midnight" />

        <p className="text-caption text-on-midnight-muted">{t('footer.rights')}</p>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <nav aria-label={title} className="flex min-w-0 flex-col gap-3.5">
      <h2 className="text-overline font-semibold uppercase text-on-midnight-muted">{title}</h2>
      {children}
    </nav>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="text-body-sm text-on-midnight hover:opacity-70">
      {children}
    </Link>
  );
}
